import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Address, Place } from '../../models/ziphy';
import { Filter, API as ZiphyAPI } from '../../utils/ZiphyAPI';
import { useQuery } from '@tanstack/react-query';
import FetchAutocomplete from '../schedule/FetchAutocomplete';
import { AutocompleteRenderInputParams, TextField } from '@mui/material';
import { parseAddress } from '../../utils/address';


async function getZiphyPlaces(practiceId: number, inputValue: string, address?:Address, placeId?:number): Promise<Place[]> {

    const filter:Filter = {}; 
    if(address) {
        filter.or = [];
        if (address.line && address.line.length >= 1) {
            const [building, street] = parseAddress(address.line[0]);
            const andFilter:Filter = {};
            andFilter.and = [];
            if(building && building.trim() !== '') { andFilter.and.push({ eq: ['building', building] }); }
            if(street && street.trim() !== '') { andFilter.and.push({ eq: ['street', street] }); }
            filter.or.push(andFilter);
        }
        else if (address.city) { filter.or.push({ eq: ['city', address.city] }); }
        else if (address.state) { filter.or.push({ eq: ['state', address.state] }); }
        else if (address.postalCode && /\d\d\d\d\d\d*/.test(address.postalCode)) { filter.or.push({ eq: ['zip', address.postalCode] }); }
    } else if (inputValue) {
        const parts = inputValue.toLowerCase().split(',').map(word => word.trim());
        filter.or = [
            ...parts.flatMap<Filter>(part => {
                if (/\d\d\d\d\d\d*/.test(part)) {
                    return [{ eq: ['zip', part] }];
                }
                if (/\d+/.test(part)) {
                    const [building, street, _apartment] = parseAddress(part);
                    return [
                        ...(building ? [{ eq: ['building', building] } as Filter] : []),
                        { imatch: ['street', street] }
                    ];
                }
                return { imatch: ['city', part] };
            })
        ]
    }

    if(placeId) {
        if(!filter.or) {
            filter.or = [];
        }
        filter.or.push({ eq: ['id', placeId] });
    }

    return (await ZiphyAPI.getPlaces({
        and: [
            { eq: ['is_hidden', false] },
            { contains: ["practice_ids", [practiceId]] },
            filter
        ]
    })).places.items;
}

interface ZiphyPlaceInputProps {
    placeholder?:string;
    disabled?:boolean;
    placeId?:number;
    practiceId: number;
    initialAddress?: Address;
    value: Place | null;
    onChange?: (value: Place | null) => void;
}

const ZiphyPlaceInput = ({ placeholder, placeId, practiceId, initialAddress, value, onChange, disabled }: ZiphyPlaceInputProps): JSX.Element => {
    const [placeInput, setPlaceInput] = useState('');
    const firstFetch = useRef(true);
    const firstSet = useRef(true);

    useEffect(() => {
        firstFetch.current = true;
        firstSet.current = true;
    }, [placeId]);

    const placesQuery = useQuery({
        enabled: !disabled,
        queryKey: ['places', practiceId, placeId, placeInput, initialAddress],
        queryFn: async () => {
            const req = await getZiphyPlaces(practiceId, placeInput, firstFetch.current ? initialAddress : undefined, placeId);
            firstFetch.current = false;
            return req;
        },
        select:(data) => {
            const placesList = data || [];
            if(!placeId) {
                return placesList;
            }
            const patientPlace = placesList.find((p) => p.id == placeId);            
            if(!patientPlace) {
                return placesList;
            }
            if(!value && firstSet.current) {
                onChange && onChange(patientPlace);
                firstSet.current = false;
            }
            return [ patientPlace, ...(placesList.filter((p) => p.id != placeId)||[]) ];
        },
        staleTime:30_000
    });


    const placesList = React.useMemo(() => {
        const list = (placesQuery.data || []);
        if(value && !list.find((p) => p.id == value?.id)) {
            return [...list, ...[value]];
        }
        return list;
    }, [value, placesQuery]);

    const getPlaceLabel = useCallback((place: Place) => (
        `${place.building} ${place.street}, ${place.city}, ${place.state}, ${place.zip}`
    ), []);

    const optEqual = useCallback((x: Place, y: Place) => x.id == y.id, []);

    const renderInput = useCallback((params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={placeholder || 'Place'} fullWidth variant='standard' />
    ), [placeholder]);

    const placeGrouping = useCallback((activePlaceId:number, currentPlaceId:number, place:Place) => {
        if(!place) {
            return 'Available';
        }
        if(place.id == currentPlaceId) {
            'Current Place';
        }
        if(place.id == activePlaceId) {
            return 'Patient Places';
        }
        return 'Available';
    },[])

    return (
        <FetchAutocomplete
            disabled={disabled}
            options={placesList}
            value={value}
            onChange={onChange}
            onInputChange={setPlaceInput}
            getOptionLabel={getPlaceLabel}
            groupBy={(place) => placeGrouping(placeId || 0, value?.id || 0, place)}
            isOptionEqualToValue={optEqual}
            renderOption={(props, option) => {return (<li {...props} key={option.id}> {getPlaceLabel(option)}</li>);}}
            renderInput={renderInput}
        />
    );
}
export default ZiphyPlaceInput;